/** EW-RUNTIME-PROBE — 移除见 ew-bg/utils/probe/TAG.md */
/**
 * 前端运行时探针上报器。
 *
 * 构建期 `ew-probe` babel 插件会在每个函数入口与分支插入 `__ewp` / `__ewb`，
 * 这里提供这两个全局函数，并周期性把命中位图发回后端，用于统计从未执行的函数
 * 与只走一侧的分支。
 *
 * 几个刻意的取舍：
 *
 * - **命中只记有无、不记次数**：用普通数组按下标写 1，比 Set.add 更快，热路径
 *   ( 渲染、滚动 ) 上的额外开销接近一次数组赋值
 * - **全量位图上报，不做增量去重**：位图按点数 / 8 计，几 KB 而已，后端按位或
 *   合并，天然幂等；省掉 localStorage 状态和随之而来的一致性问题
 * - **位图无变化就不发**：页面稳定后自然停止上报
 * - 只在 `VUE_APP_PROBE=1` 构建时装载，其余构建里整块会被 terser 消除
 */

const REPORT_URL = "/api/probe/fe";
const REPORT_INTERVAL_MS = 60_000;

/** fileId -> 稀疏数组，下标即插桩点序号，命中写 1 */
const hits = Object.create(null);
let lastSignature = "";

function markFunc(fileId, index) {
  const bucket = hits[fileId] || (hits[fileId] = []);
  bucket[index] = 1;
}

/** 位图转 base64：每 8 个插桩点压成一个字节 */
function toBase64(bucket) {
  const size = Math.ceil(bucket.length / 8);
  let binary = "";
  for (let byte = 0; byte < size; byte += 1) {
    let value = 0;
    for (let bit = 0; bit < 8; bit += 1) {
      if (bucket[byte * 8 + bit]) value |= 1 << bit;
    }
    binary += String.fromCharCode(value);
  }
  return btoa(binary);
}

function buildPayload() {
  const bitmaps = {};
  for (const fileId in hits) {
    bitmaps[fileId] = toBase64(hits[fileId]);
  }
  return {
    target: process.env.VUE_APP_PROBE_TARGET || "app",
    // 插桩点序号与本次构建绑定，服务端按 buildId 分桶，勿与其它构建混算
    buildId: process.env.VUE_APP_PROBE_BUILD || "unknown",
    path: location.pathname,
    hits: bitmaps,
  };
}

function report() {
  const payload = buildPayload();
  const body = JSON.stringify(payload);
  // 位图没变说明没有新的代码路径被走到，无需重复上报
  if (body === lastSignature) return;
  lastSignature = body;

  const blob = new Blob([body], { type: "application/json" });
  if (navigator.sendBeacon && navigator.sendBeacon(REPORT_URL, blob)) return;
  // sendBeacon 在部分场景会拒绝（队列满 / 体积超限），退回 keepalive fetch
  fetch(REPORT_URL, { method: "POST", body: blob, keepalive: true }).catch(() => {});
}

function install() {
  globalThis.__ewp = markFunc;
  globalThis.__ewb = markFunc;

  setInterval(report, REPORT_INTERVAL_MS);
  // 关标签 / 切后台是最后的上报机会，unload 已不可靠，用 pagehide + visibilitychange
  addEventListener("pagehide", report);
  addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") report();
  });
}

// 纯副作用模块，不 export 任何东西：未开启探针时 terser 可把整块死代码消除干净
if (process.env.VUE_APP_PROBE === "1") {
  install();
}
